import React, { useEffect, useRef, useState, KeyboardEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import SQLCard        from "../components/SQLCard";
import kvsLogo from "../../../assets/kvsLogo.svg";
import LoadingStages  from "../components/LoadingStages";
import ChartRenderer  from "../components/ChartRenderer";
import SummaryCard    from "../components/SummaryCard";
import MetricCards    from "../components/MetricCards";
import { useAnalyticsChat } from "../context/AnalyticsChatContext";
import {
  AssistantChatMessage,
  ChatMessage,
  PRESET_QUERIES,
  QueryPart,
  ReportMetric,
  SchoolReport,
  UserChatMessage,
  VisualizationConfig,
} from "../types";

const AI_BASE_URL = process.env.REACT_APP_AI_API_URL;

const FALLBACK_TABLE: VisualizationConfig = { type: "table", title: "Results", xAxis: "", series: [] };

// ── Bot avatar icon ───────────────────────────────────────────────────────────

const BotAvatar = () => (
  <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center bg-white shadow-sm border border-gray-100 p-0.5">
    <img src={kvsLogo} alt="KVS Logo" className="w-full h-full object-contain" />
  </div>
);

// ── Typing dots (shown while stage = "thinking") ──────────────────────────────

const TypingDots = () => (
  <div className="flex items-center gap-1 px-4 py-3 bg-white border border-gray-100 rounded-2xl rounded-tl-sm shadow-sm w-fit">
    {[0, 1, 2].map(i => (
      <span
        key={i}
        className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </div>
);

// ── School report card ────────────────────────────────────────────────────────

/** How a metric compares, worded the right way round for its direction. */
const comparisonText = (m: ReportMetric): string => {
  if (m.percentile === null || m.national_median === null) return m.note || "";
  // Phrased from the school's position on the distribution, whichever side it
  // sits on. Phrasing it from the metric's "good" direction produced
  // "Staff on roll: 64 · lower than 5% of KVs" under a green strength bar —
  // technically accurate, reads as a deficit. Whether the position is good or
  // bad is the verdict's job, not the sentence's.
  const p = m.percentile;
  return p >= 50
    ? `national median ${m.national_median} · higher than ${Math.round(p)}% of KVs`
    : `national median ${m.national_median} · lower than ${Math.round(100 - p)}% of KVs`;
};

/* Three levels, not two. "critical" is a statutory lapse — a certificate
   expired or never held — and must not read the same as a figure that happens
   to sit in the bottom quarter. Amber for the ranked findings keeps the strong
   red meaning "someone has to answer for this". */
const VERDICT_STYLE: Record<ReportMetric["verdict"], string> = {
  critical:  "border-l-4 border-l-red-700 bg-red-50",
  attention: "border-l-4 border-l-amber-500 bg-amber-50/60",
  strength:  "border-l-4 border-l-emerald-400 bg-emerald-50/60",
  neutral:   "border-l-4 border-l-gray-200",
  unknown:   "border-l-4 border-l-gray-200 opacity-60",
};

const MetricRow: React.FC<{ metric: ReportMetric }> = ({ metric }) => (
  <div className={"flex flex-wrap items-baseline gap-x-2 px-3 py-2 rounded-r-lg " + VERDICT_STYLE[metric.verdict]}>
    <span className="text-sm text-gray-700">{metric.label}</span>
    <span className="text-sm font-semibold text-gray-900">
      {/* Compliance particulars have no number — the finding IS the text
          ("Not held", "Lapsed 23-12-2025"). A dash here would erase it. */}
      {metric.text_value ?? (metric.value === null ? "—" : `${metric.value}${metric.unit}`)}
    </span>
    <span className="basis-full text-[11px] text-gray-500">{comparisonText(metric)}</span>
  </div>
);

const SchoolReportCard: React.FC<{ report: SchoolReport }> = ({ report }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
      <div>
        <p className="text-sm font-bold text-gray-900">{report.school_name}</p>
        <p className="text-[11px] text-gray-500">{report.region} region · school report</p>
      </div>
      {/* Hands over to the official document at /analytics/school-report,
          which opens this school directly and prints to PDF via the browser's
          print dialog. The chat card is a summary; the document is the
          deliverable. */}
      <Link
        to={`/analytics/school-report?school=${report.school_id}`}
        className="shrink-0 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5
          text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
      >
        Open printable report
      </Link>
    </div>

    <div className="p-4 space-y-4">
      {report.sections.map(section => (
        <div key={section.key}>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {section.title}
          </p>

          {/* An unreachable section is stated, never omitted: a silently missing
              Staff block reads as a school with nothing to say about its staff. */}
          {section.unavailable ? (
            <p className="px-3 py-2 text-xs text-gray-500 bg-gray-50 rounded-lg">
              {section.unavailable}
            </p>
          ) : (
            <div className="space-y-1">
              {Object.entries(section.facts)
                .filter(([, v]) => v !== null && v !== undefined && !Array.isArray(v))
                .map(([k, v]) => (
                  <div key={k} className="flex gap-2 px-3 text-xs">
                    <span className="text-gray-500">{k}</span>
                    <span className="font-medium text-gray-800">{String(v)}</span>
                  </div>
                ))}
              {section.metrics.map(m => <MetricRow key={m.label} metric={m} />)}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ── Multi-part answer ─────────────────────────────────────────────────────────

/**
 * One sub-question and its own answer.
 *
 * Rendered as separate blocks on purpose. The server never combines figures
 * across parts, and neither does this: each number sits under the question it
 * answers, with the SQL that produced it.
 */
const PartBlock: React.FC<{ part: QueryPart; index: number }> = ({ part, index }) => (
  <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
    <div className="flex items-baseline gap-2 border-b border-gray-100 px-4 py-2.5">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full
        bg-indigo-100 text-[10px] font-bold text-indigo-700">
        {index + 1}
      </span>
      <p className="text-sm font-medium text-gray-800">{part.question}</p>
    </div>

    <div className="space-y-3 p-4">
      {part.error ? (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{part.error}</p>
      ) : (
        <>
          {/* rowCount is what the table SHOWS, row_count is what the query
              matched. Passing the total as rowCount would leave isTruncated
              false, so a part capped at MAX_ROWS would silently claim to be
              showing everything. */}
          {part.summary && (
            <SummaryCard
              summary={part.summary}
              rowCount={part.data.length}
              totalCount={part.row_count}
            />
          )}
          {part.sql_query && <SQLCard sql={part.sql_query} />}
          {part.data.length > 0 && (
            <div className="rounded-xl border border-gray-100 p-3">
              <ChartRenderer
                config={part.visualization_config ?? FALLBACK_TABLE}
                data={part.data}
              />
            </div>
          )}
          {part.download_url && (
            <a
              href={`${AI_BASE_URL}${part.download_url}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200
                px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Download CSV ({part.row_count.toLocaleString()} rows)
            </a>
          )}
        </>
      )}
    </div>
  </div>
);

// ── Single assistant response bubble ─────────────────────────────────────────

const AssistantBubble: React.FC<{
  msg: AssistantChatMessage;
  /** Sends a suggested answer as the next turn. The backend resolves it against
   *  the stored session, so the short reply ("Below 75%") is enough on its own. */
  onOptionClick?: (option: string) => void;
  /** Re-runs this turn's stored SQL to refill its table. */
  onRerun?: (messageId: string) => void;
}> = ({ msg, onOptionClick, onRerun }) => {
  const isThinking = msg.stage === "thinking";
  const isLoading  = ["sql", "data", "summary"].includes(msg.stage);
  const hasData    = msg.data !== null && (msg.data?.length ?? 0) > 0;
  const vizConfig  = msg.visualizationConfig ?? FALLBACK_TABLE;

  return (
    <div className="flex items-start gap-3">
      <BotAvatar />
      <div className="flex-1 min-w-0 space-y-3 max-w-[calc(100%-44px)]">

        {isThinking && <TypingDots />}

        {(isLoading || (msg.stage === "error" && !msg.error)) && (
          <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm shadow-sm px-4 py-3">
            <LoadingStages stage={msg.stage} />
          </div>
        )}

        {/* The copilot asked a question instead of answering. Deliberately styled
            as a normal assistant message rather than an error: it is a reasonable
            reply, and red would train users to read it as a failure. */}
        {msg.stage === "clarify" && msg.clarifyQuestion && (
          <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm
            shadow-sm px-4 py-3 text-sm text-gray-800">
            <div className="flex items-start gap-2.5">
              <svg className="w-4 h-4 mt-0.5 shrink-0 text-indigo-500" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3
                     0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21
                     12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p>{msg.clarifyQuestion}</p>
                {msg.clarifyOptions.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {msg.clarifyOptions.map(option => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => onOptionClick?.(option)}
                        className="px-3 py-1.5 text-xs font-medium rounded-full border
                          border-indigo-200 text-indigo-700 bg-indigo-50
                          hover:bg-indigo-100 hover:border-indigo-300
                          focus:outline-none focus:ring-2 focus:ring-indigo-300
                          transition-colors"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  {msg.clarifyOptions.length > 0
                    ? "Pick one, or type your own answer below."
                    : "Reply below and I'll use it with your original question."}
                </p>
              </div>
            </div>
          </div>
        )}

        {msg.stage === "error" && msg.error && (
          <div className="flex items-start gap-2.5 bg-red-50 border border-red-100
            rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-red-700">
            <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {msg.error}
          </div>
        )}

        {/* The planner decided this is really two questions. Naming them while
            they run gives the wait something to show, and lets the user see the
            split was sensible before the figures arrive. */}
        {msg.plannedParts && !msg.parts && (
          <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 px-4 py-3">
            <p className="text-xs font-medium text-indigo-800">
              Answering this as {msg.plannedParts.length} separate questions:
            </p>
            <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-xs text-indigo-700">
              {msg.plannedParts.map(q => <li key={q}>{q}</li>)}
            </ol>
          </div>
        )}

        {msg.parts && (
          <div className="space-y-3">
            {msg.parts.map((p, i) => <PartBlock key={p.question + i} part={p} index={i} />)}
            <p className="px-1 text-[11px] text-gray-400">
              Answered as separate queries. Figures are not combined across them —
              each sits under the question it answers.
            </p>
          </div>
        )}

        {/* Plain conversational reply. Rendered without the "AI Analysis"
            framing of a data answer, because no data was read. */}
        {msg.message && (
          <div className="whitespace-pre-line rounded-2xl rounded-tl-sm border border-gray-100
            bg-white px-4 py-3 text-sm leading-relaxed text-gray-800 shadow-sm">
            {msg.message}
          </div>
        )}

        {msg.report && <SchoolReportCard report={msg.report} />}

        {msg.sqlQuery && <SQLCard sql={msg.sqlQuery} />}

        {(hasData || msg.stats) && <MetricCards data={msg.data ?? []} stats={msg.stats} />}

        {/* Server-computed rollup (e.g. attendance rate per RO) -- rendered
            before the detail table because the aggregate IS the answer; the
            5,000 detail rows below it are the evidence. Has its own dataset. */}
        {msg.insightChart && (
          <div className="lg:hidden bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <ChartRenderer
              key={`insight-${msg.insightChart.config.title}`}
              config={msg.insightChart.config}
              data={msg.insightChart.data}
            />
          </div>
        )}

        {/* Result rows are never stored — thousands of student/staff records per
            answer, which would also age into stale figures shown as current. So
            a reopened chat arrives with no table. It used to render nothing at
            all here and the chart simply vanished; now the gap is visible and
            fillable. */}
        {!hasData && msg.stage === "done" && !msg.report && !msg.parts && !msg.message && (
          <div className="bg-white rounded-2xl border border-dashed border-gray-300 px-4 py-3.5">
            {msg.turnId != null ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <p className="text-sm text-gray-600">
                  Results aren’t kept when a chat is reopened.
                </p>
                <button
                  type="button"
                  onClick={() => onRerun?.(msg.id)}
                  disabled={msg.rerunState === "loading"}
                  className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5
                    text-xs font-semibold text-indigo-700 hover:bg-indigo-100
                    disabled:opacity-50"
                >
                  {msg.rerunState === "loading" ? "Loading…" : "Load results"}
                </button>
                <span className="basis-full text-[11px] leading-tight text-gray-400">
                  Runs the same query again, so figures may have moved on from
                  the summary above.
                </span>
                {msg.rerunState === "error" && msg.rerunError && (
                  <span className="basis-full text-[11px] leading-tight text-red-600">
                    {msg.rerunError}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Results aren’t available for this answer. Ask the question again
                to see the table.
              </p>
            )}
          </div>
        )}

        {/* A fresh answer already has its table — the empty-state "Load results"
            button above never renders for it. Without this, getting updated
            figures for a question already answered meant editing the query
            back to its own wording just to force a resend. This re-runs the
            same stored SQL the "Load results" button does, just from a
            state that already has rows rather than none. */}
        {hasData && msg.turnId != null && msg.stage === "done" && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1">
            <button
              type="button"
              onClick={() => onRerun?.(msg.id)}
              disabled={msg.rerunState === "loading"}
              className="inline-flex items-center gap-1 text-[11px] font-semibold
                text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0
                     0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {msg.rerunState === "loading" ? "Reloading…" : "Reload"}
            </button>
            {/* Reloaded rows are not the rows the summary was written about. Say
                so rather than letting the two quietly disagree. */}
            {msg.rerunAt !== null && (
              <span className="text-[11px] text-amber-700">
                Reloaded {new Date(msg.rerunAt).toLocaleTimeString()} — the
                summary above still describes the figures from when the
                question was first asked.
              </span>
            )}
            {msg.rerunState === "error" && msg.rerunError && (
              <span className="text-[11px] text-red-600">{msg.rerunError}</span>
            )}
          </div>
        )}

        {/* The data table always stays inline; pure charts move to the side
            visualiser on lg+ screens, so only hide non-table configs there. */}
        {hasData && (
          <div className={"bg-white rounded-2xl border border-gray-100 shadow-sm p-4 " + (vizConfig.type !== "table" ? "lg:hidden" : "")}>
            <ChartRenderer key={vizConfig.type} config={vizConfig} data={msg.data!} />
          </div>
        )}

        {/* Summary skeleton */}
        {hasData && !msg.summary && msg.stage !== "error" && msg.stage !== "done" && (
          <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm shadow-sm
            border-l-4 border-l-blue-200 px-5 py-4 space-y-2 animate-pulse">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-300 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              <span className="text-xs font-semibold text-blue-400">Analysing results…</span>
            </div>
            <div className="space-y-1.5">
              <div className="h-3 bg-gray-100 rounded w-full"/>
              <div className="h-3 bg-gray-100 rounded w-5/6"/>
              <div className="h-3 bg-gray-100 rounded w-4/6"/>
            </div>
          </div>
        )}

        {msg.summary && (
          <SummaryCard
            summary={msg.summary}
            rowCount={msg.rowCount}
            totalCount={msg.totalCount}
          />
        )}

        {/* "You could also ask" — every chip is a question the backend is
            proven to answer, so a click can never land on an error. */}
        {msg.stage === "done" && msg.followups.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-1">
            <span className="text-[11px] text-gray-400">You could also ask:</span>
            {msg.followups.map(question => (
              <button
                key={question}
                type="button"
                onClick={() => onOptionClick?.(question)}
                className="rounded-full border border-gray-200 bg-white px-3 py-1.5
                  text-xs text-gray-700 shadow-sm transition-colors hover:border-indigo-300
                  hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none
                  focus:ring-2 focus:ring-indigo-200"
              >
                {question}
              </button>
            ))}
          </div>
        )}

        {msg.downloadUrl && (
          <div className="flex">
            <a
              href={`${AI_BASE_URL}${msg.downloadUrl}`}
              download="kvs_analytics_export.csv"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl
                border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-medium
                hover:bg-emerald-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
              </svg>
              Download CSV
              {(msg.totalCount ?? msg.rowCount) !== null && (
                <span className="text-xs opacity-70">
                  ({(msg.totalCount ?? msg.rowCount)!.toLocaleString()} records)
                </span>
              )}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

// ── User message bubble ───────────────────────────────────────────────────────

const UserBubble: React.FC<{
  query: string;
  /** Omitted for a read-only bubble. */
  onEdit?: (text: string) => void;
  /** True while an answer is streaming -- editing mid-stream would abort it. */
  busy?: boolean;
}> = ({ query, onEdit, busy }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(query);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  // Reset the draft each time the editor opens, so a cancelled edit does not
  // survive to the next one, and put the caret at the end rather than the start.
  useEffect(() => {
    if (!editing) return;
    setDraft(query);
    const id = window.requestAnimationFrame(() => {
      const el = areaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
    return () => window.cancelAnimationFrame(id);
  }, [editing, query]);

  const submit = () => {
    const text = draft.trim();
    setEditing(false);
    // Nothing to re-run if the wording did not actually change.
    if (!text || text === query.trim()) return;
    onEdit?.(text);
  };

  if (editing) {
    return (
      <div className="flex justify-end">
        <div className="w-full sm:w-[85%] rounded-2xl border border-blue-300 bg-white p-3 shadow-sm">
          <textarea
            ref={areaRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
              if (e.key === "Escape") setEditing(false);
            }}
            rows={Math.min(6, draft.split(/\r?\n/).length + 1)}
            maxLength={500}
            className="w-full resize-none bg-transparent text-sm leading-relaxed text-gray-800 outline-none"
            aria-label="Edit your question"
          />
          <div className="mt-2 flex items-center gap-2">
            <span className="mr-auto text-[10px] leading-tight text-gray-400">
              Replaces this question. Everything below it is removed.
            </span>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!draft.trim()}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
              style={{ background: "linear-gradient(135deg,#7C3AED 0%,#2563EB 100%)" }}
            >
              Ask again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-start justify-end gap-1">
      {onEdit && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          disabled={busy}
          title={busy ? "Wait for the current answer to finish" : "Edit and ask again"}
          aria-label="Edit and ask again"
          className="mt-2 rounded-lg p-1.5 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-600 focus:opacity-100 focus-visible:outline focus-visible:outline-2 group-hover:opacity-100 disabled:pointer-events-none disabled:opacity-0"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth={2}
               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
          </svg>
        </button>
      )}
      <div
        className="max-w-[75%] px-4 py-3 rounded-2xl rounded-tr-sm text-sm text-white leading-relaxed"
        style={{ background: "linear-gradient(135deg,#7C3AED 0%,#2563EB 100%)" }}
      >
        {query}
      </div>
    </div>
  );
};

// ── Welcome / empty state ─────────────────────────────────────────────────────

const WelcomeScreen: React.FC<{ onSelect: (q: string) => void }> = ({ onSelect }) => (
  <div className="flex flex-col items-center justify-center h-full px-4 py-10 space-y-8">
    {/* Logo + title */}
    <div className="text-center space-y-3">
      <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg bg-white border border-gray-100 p-2">
        <img src={kvsLogo} alt="KVS Logo" className="w-full h-full object-contain" />
      </div>
      <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Montserrat, sans-serif" }}>
        Samiksha Samadhan
      </h1>
      <p className="text-sm text-gray-500 max-w-sm leading-relaxed">
        Ask any question about KVS student performance, employee attendance, or school data in plain English.
      </p>
    </div>

    {/* Preset query chips */}
    <div className="w-full max-w-2xl">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 text-center">
        Try asking
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {PRESET_QUERIES.map((pq) => (
          <button
            key={pq.label}
            onClick={() => onSelect(pq.query)}
            className="text-left px-4 py-3 rounded-xl border border-gray-200 bg-white
              hover:border-indigo-300 hover:bg-indigo-50 transition-colors group shadow-sm"
          >
            <span className="text-xs font-semibold text-indigo-600 group-hover:text-indigo-700 block mb-0.5">
              {pq.label}
            </span>
            <span className="text-xs text-gray-500 leading-relaxed line-clamp-2">
              {pq.query}
            </span>
          </button>
        ))}
      </div>
    </div>
  </div>
);

// ── Chat input bar ────────────────────────────────────────────────────────────

interface ChatInputBarProps {
  onSend: (text: string) => void;
  disabled: boolean;
}

const ChatInputBar: React.FC<ChatInputBarProps> = ({ onSend, disabled }) => {
  const [text, setText] = useState("");
  const taRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = () => {
    const ta = taRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
    }
  };

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText("");
    // Reset height
    if (taRef.current) taRef.current.style.height = "auto";
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex items-end gap-3 bg-white border border-gray-200 rounded-2xl
      px-4 py-3 shadow-md focus-within:border-indigo-400 transition-colors">
      <textarea
        ref={taRef}
        value={text}
        rows={1}
        onChange={e => { setText(e.target.value); autoResize(); }}
        onKeyDown={handleKey}
        disabled={disabled}
        placeholder="Ask about attendance, scores, enrollment… (Enter to send, Shift+Enter for newline)"
        className="flex-1 resize-none bg-transparent text-sm text-gray-800
          placeholder-gray-400 outline-none leading-relaxed disabled:opacity-60"
        style={{ maxHeight: 160 }}
      />
      <button
        onClick={handleSend}
        disabled={disabled || !text.trim()}
        className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center
          transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          background: disabled || !text.trim()
            ? "#E5E7EB"
            : "linear-gradient(135deg,#7C3AED 0%,#2563EB 100%)",
        }}
      >
        {disabled ? (
          <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
        ) : (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
              d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        )}
      </button>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const AnalyticsCopilotPage: React.FC = () => {
  const navigate = useNavigate();
  const { messages, isRunning, sendQuery, editQuery, rerunTurn, loadSessions, reset, chats, activeChatId, selectChat, deleteChat } = useAnalyticsChat();

  // The chat list is this screen's concern, so it is fetched here rather than
  // by the provider, which is mounted on every authenticated page.
  useEffect(() => { void loadSessions(); }, [loadSessions]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const hasMessages = messages.length > 0;

  // Width of the side visualiser as a percentage of the window, draggable via
  // the divider and remembered across sessions. Clamped so neither the chat nor
  // the panel can be dragged into uselessness.
  const [panelPct, setPanelPct] = useState<number>(() => {
    const saved = Number(localStorage.getItem("vizPanelPct"));
    return saved >= 25 && saved <= 65 ? saved : 42;
  });
  // Width of the Recents sidebar in pixels, draggable the same way. Pixels
  // (not %) because the list reads best at a roughly constant width.
  const [sidebarPx, setSidebarPx] = useState<number>(() => {
    const saved = Number(localStorage.getItem("recentsSidebarPx"));
    return saved >= 180 && saved <= 420 ? saved : 240;
  });
  // Which divider is being dragged right now: false, "viz" or "sidebar".
  const draggingRef = useRef<false | "viz" | "sidebar">(false);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (draggingRef.current === "viz") {
        const pct = ((window.innerWidth - e.clientX) / window.innerWidth) * 100;
        setPanelPct(Math.min(65, Math.max(25, pct)));
      } else if (draggingRef.current === "sidebar") {
        setSidebarPx(Math.min(420, Math.max(180, e.clientX)));
      }
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      const which = draggingRef.current;
      draggingRef.current = false;
      document.body.style.userSelect = "";
      if (which === "viz") {
        setPanelPct(current => {
          localStorage.setItem("vizPanelPct", String(current));
          return current;
        });
      } else {
        setSidebarPx(current => {
          localStorage.setItem("recentsSidebarPx", String(current));
          return current;
        });
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  // The latest assistant message that has something chartable. Walked backwards
  // so the panel always mirrors the answer the user is currently looking at;
  // the question text comes from the preceding user bubble for the header.
  const sideCharts = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "assistant") continue;
      const a = m as AssistantChatMessage;
      const chartViz = a.visualizationConfig && a.visualizationConfig.type !== "table"
        ? a.visualizationConfig : null;
      if (!a.insightChart && !chartViz) continue;
      let question = "";
      for (let j = i - 1; j >= 0; j--) {
        if (messages[j].role === "user") { question = (messages[j] as UserChatMessage).query; break; }
      }
      return {
        question,
        insightChart: a.insightChart,
        viz: chartViz,
        data: chartViz ? a.data : null,
      };
    }
    return null;
  }, [messages]);

  // Auto-scroll to bottom as new content arrives
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="flex flex-col bg-gray-50" style={{ height: "100dvh" }}>

      {/* ── Header bar ─────────────────────────────────────────────────────── */}
      <div
        className="shrink-0 flex items-center justify-between px-4 h-14 border-b border-white/20 shadow-sm"
        style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4F46E5 55%,#2563EB 100%)" }}
      >
        <div className="flex items-center gap-2.5">
          {/* The copilot fills the viewport with its own chrome, so the app's
              usual navigation is off screen while you are in here. Goes back
              through history when there is somewhere to go back to, and falls
              through to the dashboard when the page was opened directly. */}
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate("/dashboard");
            }}
            title="Back"
            aria-label="Go back"
            className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/15
              hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                 aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <BotAvatar />
          <div>
            <p className="text-sm font-bold text-white leading-tight">Samiksha Samadhan</p>
            <p className="text-[10px] text-white/65">KVS AI Analytics Copilot</p>
          </div>
        </div>

        {hasMessages && (
          <button
            onClick={reset}
            className="flex items-center gap-1.5 text-xs text-white/80 hover:text-white
              bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 4v16m8-8H4"/>
            </svg>
            New chat
          </button>
        )}
      </div>

      {/* ── Chat column + side visualiser (lg+) ─────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">

        {/* Recents sidebar (md+). Purely local: conversations live in this
            browser's localStorage until Phase 2 moves them server-side. */}
        {chats.length > 0 && (
          <nav
            className="hidden md:flex flex-col shrink-0 border-r border-gray-200 bg-white"
            style={{ width: sidebarPx }}
          >
            <div className="px-4 py-3 border-b border-gray-100">
              <button
                onClick={reset}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium
                  text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                </svg>
                New chat
              </button>
            </div>
            <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              Recents
            </p>
            <div className="flex-1 overflow-y-auto px-2 pb-2">
              {chats.map((c) => (
                <div
                  key={c.id}
                  className={"group flex items-center gap-1 rounded-lg " +
                    (c.id === activeChatId ? "bg-indigo-50" : "hover:bg-gray-50")}
                >
                  <button
                    onClick={() => selectChat(c.id)}
                    className={"flex-1 min-w-0 text-left px-3 py-2 text-sm truncate " +
                      (c.id === activeChatId ? "text-indigo-800 font-medium" : "text-gray-700")}
                    title={c.title}
                  >
                    {c.title}
                  </button>
                  <button
                    onClick={() => deleteChat(c.id)}
                    className="opacity-0 group-hover:opacity-100 shrink-0 p-1.5 mr-1 rounded
                      text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all"
                    title="Delete conversation"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </nav>
        )}
        {/* Drag to resize the Recents sidebar. */}
        {chats.length > 0 && (
          <div
            className="hidden md:block w-1.5 shrink-0 cursor-col-resize bg-gray-200
              hover:bg-indigo-400 active:bg-indigo-500 transition-colors"
            title="Drag to resize"
            onPointerDown={() => {
              draggingRef.current = "sidebar";
              document.body.style.userSelect = "none";
            }}
          />
        )}

        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto">
            {!hasMessages ? (
              <WelcomeScreen onSelect={q => sendQuery(q)} />
            ) : (
              <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
                {messages.map((msg: ChatMessage) =>
                  msg.role === "user"
                    ? <UserBubble
                        key={msg.id}
                        query={msg.query}
                        busy={isRunning}
                        onEdit={text => editQuery(msg.id, text)}
                      />
                    : <AssistantBubble
                        key={msg.id}
                        msg={msg as AssistantChatMessage}
                        onOptionClick={sendQuery}
                        onRerun={rerunTurn}
                      />
                )}
                <div ref={bottomRef} />
              </div>
            )}
          </div>

          <div className="shrink-0 bg-gray-50 border-t border-gray-200 px-4 py-3">
            <div className="max-w-3xl mx-auto">
              <ChatInputBar onSend={sendQuery} disabled={isRunning} />
              <p className="text-center text-[10px] text-gray-400 mt-2">
                AI may make mistakes. Verify critical figures against source data.
              </p>
            </div>
          </div>
        </div>

        {/* Side visualiser: pins the LATEST answer's charts beside the chat on
            wide screens, so the graph stays in view while the conversation
            scrolls. On smaller screens the same charts render inline instead. */}
        {sideCharts && (
          <>
            {/* Drag to resize the chat / visualisation split. */}
            <div
              className="hidden lg:block w-1.5 shrink-0 cursor-col-resize bg-gray-200
                hover:bg-indigo-400 active:bg-indigo-500 transition-colors"
              title="Drag to resize"
              onPointerDown={() => {
                draggingRef.current = "viz";
                document.body.style.userSelect = "none";
              }}
            />
            <aside
              className="hidden lg:flex flex-col shrink-0 border-l border-gray-200 bg-white"
              style={{ width: `${panelPct}%` }}
            >
              <div className="px-5 py-3 border-b border-gray-100 shrink-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Visualisation
                </p>
                <p className="text-sm text-gray-800 truncate" title={sideCharts.question}>
                  {sideCharts.question}
                </p>
              </div>
              {/* min-h-0 lets the last card claim ALL remaining height, so a
                  table in the panel runs the full screen instead of a short card. */}
              <div className="flex-1 min-h-0 flex flex-col p-4 gap-4 overflow-y-auto">
                {sideCharts.insightChart && (
                  <div className="rounded-xl border border-gray-100 p-3 shrink-0">
                    <ChartRenderer
                      key={`side-insight-${sideCharts.insightChart.config.title}`}
                      config={sideCharts.insightChart.config}
                      data={sideCharts.insightChart.data}
                      pageSize={100}
                    />
                  </div>
                )}
                {/* Rows were dropped when the chat was saved, so there is
                    nothing to plot until the answer below is reloaded. Without
                    this the panel is a bare heading over blank space. */}
                {sideCharts.viz && !sideCharts.data && (
                  <div className="rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                    Chart data isn’t kept when a chat is reopened. Use
                    <span className="font-medium text-gray-700"> Load results </span>
                    on the answer to draw it again.
                  </div>
                )}
                {sideCharts.viz && sideCharts.data && (
                  <div className="rounded-xl border border-gray-100 p-3 flex-1 min-h-0 overflow-y-auto">
                    <ChartRenderer
                      key={`side-viz-${sideCharts.viz.title}`}
                      config={sideCharts.viz}
                      data={sideCharts.data}
                      pageSize={100}
                    />
                  </div>
                )}
              </div>
            </aside>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsCopilotPage;
